import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icon from "@mdi/react";
import { mdiMagnify, mdiInformationOutline } from "@mdi/js";
import { Button, CircularProgress } from "@mui/material";
import styles from "./styles.module.scss";
import { clsx } from "@ismael1361/utils";
import { McpLibraryServices, PublicMcpDTO, NotistackServices } from "shared/services";
import { HandleError } from "shared/utils";

/** Gera iniciais a partir do nome (até 2 letras). */
function getInitials(name: string): string {
	return name
		.split(/[\s-_]+/)
		.filter(Boolean)
		.map((w) => w[0].toUpperCase())
		.slice(0, 2)
		.join("");
}

export const ToolLibraryContent: React.FC = () => {
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedCategory, setSelectedCategory] = useState("Todos");
	const [mcps, setMcps] = useState<PublicMcpDTO[]>([]);
	const [loading, setLoading] = useState(true);

	const timeRef = useRef<NodeJS.Timeout>(undefined);

	const loadMcps = useCallback(() => {
		setLoading(true);
		clearTimeout(timeRef.current);
		timeRef.current = setTimeout(async () => {
			try {
				const data = await McpLibraryServices.listMcps();
				setMcps(data);
			} catch (error) {
				NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar ferramentas");
			} finally {
				setLoading(false);
			}
		}, 300);
	}, []);

	useEffect(() => {
		loadMcps();
	}, [loadMcps]);

	/** Separa categorias no formato "cat1, cat2, cat3" em array. */
	const parseCats = (raw: string | null | undefined): string[] =>
		(raw ?? "")
			.split(",")
			.map((c) => c.trim())
			.filter(Boolean);

	// Extrai categorias únicas dos MCPs carregados
	const categories = useMemo(() => {
		const cats = new Set<string>();
		for (const m of mcps) {
			for (const c of parseCats(m.category)) cats.add(c);
		}
		return ["Todos", ...Array.from(cats).sort()];
	}, [mcps]);

	const filteredTools = useMemo(() => {
		let result = mcps;
		if (selectedCategory !== "Todos") {
			result = result.filter((t) => parseCats(t.category).includes(selectedCategory));
		}
		if (searchTerm.trim()) {
			const term = searchTerm.toLowerCase();
			result = result.filter((t) => t.name.toLowerCase().includes(term) || (t.description ?? "").toLowerCase().includes(term));
		}
		return result;
	}, [selectedCategory, searchTerm, mcps]);

	return (
		<div className={styles.libraryContainer}>
			{/* Header */}
			<div className={styles.libraryHeader}>
				<h2 className={styles.libraryTitle}>Ferramentas e Serviços Online</h2>
			</div>

			{/* Search and Filters */}
			<div className={styles.searchSection}>
				<div className={styles.searchWrapper}>
					<Icon
						path={mdiMagnify}
						size={1}
						className={styles.searchIcon}
					/>
					<input
						className={styles.searchInput}
						placeholder="O que você está procurando hoje?"
						type="text"
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
					/>
				</div>
				<div className={styles.filterRow}>
					{categories.map((cat) => (
						<Button
							key={cat}
							className={clsx(styles.filterChip, selectedCategory === cat ? styles.active : styles.inactive)}
							onClick={() => setSelectedCategory(cat)}
							disableRipple
						>
							{cat}
						</Button>
					))}
				</div>
			</div>

			{/* Tool Grid */}
			{loading ? (
				<div className={styles.loadingContainer}>
					<CircularProgress size={32} />
				</div>
			) : filteredTools.length === 0 ? (
				<div className={styles.emptyContainer}>
					<p>Nenhuma ferramenta encontrada</p>
				</div>
			) : (
				<div className={styles.toolGrid}>
					{filteredTools.map((tool) => (
						<div
							key={tool.id}
							className={styles.gridCard}
						>
							<div className={styles.gridCardHeader}>
								{tool.iconUrl ? (
									<img
										src={tool.iconUrl}
										alt={tool.name}
										className={styles.gridCardLogoImg}
									/>
								) : (
									<div
										className={styles.gridCardLogo}
										style={{ backgroundColor: "var(--mui-palette-primary-main)" }}
									>
										<span>{getInitials(tool.name)}</span>
									</div>
								)}
								<button
									className={styles.gridCardInfoBtn}
									title="Mais informações"
								>
									<Icon
										path={mdiInformationOutline}
										size={0.8}
									/>
								</button>
							</div>
							<h3 className={styles.gridCardName}>{tool.name}</h3>
							<span className={styles.gridCardCategory}>{tool.category}</span>
							<p className={styles.gridCardDesc}>{tool.description ?? ""}</p>
						</div>
					))}
				</div>
			)}
		</div>
	);
};
